import { useEffect, useMemo, useRef } from 'react';

export const DATE_WHEEL_ROW_HEIGHT = 44;
export const DATE_WHEEL_SETTLE_MS = 140;
const months = [
  'Январь',
  'Февраль',
  'Март',
  'Апрель',
  'Май',
  'Июнь',
  'Июль',
  'Август',
  'Сентябрь',
  'Октябрь',
  'Ноябрь',
  'Декабрь',
];
const pad = (value: number) => String(value).padStart(2, '0');
const daysIn = (year: number, month: number) => new Date(Date.UTC(year, month, 0)).getUTCDate();

type WheelColumnProps = {
  label: string;
  values: number[];
  selected: number;
  format: (value: number) => string;
  onSelect: (value: number) => void;
};

export function nearestDateWheelIndex(scrollTop: number, length: number): number {
  return Math.max(0, Math.min(length - 1, Math.round(scrollTop / DATE_WHEEL_ROW_HEIGHT)));
}

function WheelColumn({ label, values, selected, format, onSelect }: WheelColumnProps) {
  const ref = useRef<HTMLDivElement>(null);
  const settleTimer = useRef<number | null>(null);
  const scrolling = useRef(false);
  const selectedIndex = Math.max(0, values.indexOf(selected));

  const cancelSettle = () => {
    if (settleTimer.current !== null) window.clearTimeout(settleTimer.current);
    settleTimer.current = null;
  };
  const selectNearest = () => {
    cancelSettle();
    scrolling.current = true;
    settleTimer.current = window.setTimeout(() => {
      settleTimer.current = null;
      scrolling.current = false;
      const index = nearestDateWheelIndex(ref.current?.scrollTop ?? 0, values.length);
      const next = values[index];
      if (next !== undefined && next !== selected) onSelect(next);
      else
        ref.current?.scrollTo({
          top: index * DATE_WHEEL_ROW_HEIGHT,
          behavior: 'auto',
        });
    }, DATE_WHEEL_SETTLE_MS);
  };
  const choose = (value: number) => {
    cancelSettle();
    scrolling.current = false;
    onSelect(value);
  };
  const move = (offset: number) =>
    choose(values[Math.max(0, Math.min(values.length - 1, selectedIndex + offset))]!);

  useEffect(() => {
    if (!scrolling.current)
      ref.current?.scrollTo({
        top: selectedIndex * DATE_WHEEL_ROW_HEIGHT,
        behavior: 'auto',
      });
  }, [selectedIndex]);
  useEffect(() => () => cancelSettle(), []);

  return (
    <div className="date-wheel__group">
      <span className="date-wheel__label">{label}</span>
      <div
        ref={ref}
        className="date-wheel__column"
        role="listbox"
        aria-label={label}
        tabIndex={0}
        onScroll={selectNearest}
        onKeyDown={(event) => {
          if (event.key === 'ArrowUp') {
            event.preventDefault();
            move(-1);
          }
          if (event.key === 'ArrowDown') {
            event.preventDefault();
            move(1);
          }
          if (event.key === 'Home') {
            event.preventDefault();
            choose(values[0]!);
          }
          if (event.key === 'End') {
            event.preventDefault();
            choose(values[values.length - 1]!);
          }
        }}
      >
        <div className="date-wheel__spacer" aria-hidden="true" />
        {values.map((item) => (
          <div
            key={item}
            className="date-wheel__option"
            role="option"
            aria-selected={item === selected}
            onClick={() => choose(item)}
          >
            {format(item)}
          </div>
        ))}
        <div className="date-wheel__spacer" aria-hidden="true" />
      </div>
    </div>
  );
}

export function DateWheel({
  value,
  onChange,
  id = 'birthDate',
}: {
  value: string;
  onChange: (value: string) => void;
  id?: string;
}) {
  const parts = value.split('-').map(Number);
  const years = useMemo(
    () => Array.from({ length: 83 }, (_, index) => new Date().getUTCFullYear() - 18 - index),
    [],
  );
  const year = years.includes(parts[0] ?? 0) ? parts[0]! : years[0]!;
  const monthValue = parts[1] ?? 0;
  const month = monthValue >= 1 && monthValue <= 12 ? monthValue : 1;
  const day = Math.min(Math.max(parts[2] || 1, 1), daysIn(year, month));
  const set = (nextYear = year, nextMonth = month, nextDay = day) =>
    onChange(
      `${nextYear}-${pad(nextMonth)}-${pad(Math.min(nextDay, daysIn(nextYear, nextMonth)))}`,
    );
  return (
    <div className="date-wheel" aria-label="Дата рождения">
      <input
        id={id}
        name="birthDate"
        type="hidden"
        value={`${year}-${pad(month)}-${pad(day)}`}
        readOnly
      />
      <WheelColumn
        label="День"
        values={Array.from({ length: daysIn(year, month) }, (_, index) => index + 1)}
        selected={day}
        format={pad}
        onSelect={(next) => set(year, month, next)}
      />
      <WheelColumn
        label="Месяц"
        values={months.map((_, index) => index + 1)}
        selected={month}
        format={(next) => months[next - 1]!}
        onSelect={(next) => set(year, next, day)}
      />
      <WheelColumn
        label="Год"
        values={years}
        selected={year}
        format={(next) => String(next)}
        onSelect={(next) => set(next, month, day)}
      />
    </div>
  );
}
